import React, { useState, useMemo, useEffect } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { Plus } from 'lucide-react';
import DataTable from '../../common/DataTable/index';
import Search from '../../common/Search';
import CommonDialog from '../../common/CommonDialog';
import AddProduct from './AddProduct';
import { fetchProduct } from '../../../../apis/Issues/Product';
import ExportExcel from '../../common/ExportExcel';
import { useResponsive, userScreenAccess } from '../../../../utils/MasterUtils';

let _editRowHandler = null;

const headers = [
  {
    label: 'Product Name',
    key: 'productName', // Fallback structure handles name differences if needed
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 140,
    sortable: true,
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); // null = Add mode
  const { isMobile } = useResponsive();

  const userAccess = userScreenAccess();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    if (!userAccess.hasEditPermission) return;
    setSelectedProduct(row);
    setOpenDialog(true);
  };

  // Open Add dialog
  const handleAddProduct = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedProduct(null);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
  };

  const fetchProductListResponse = async () => {
    setIsLoading(true);
    try {
      const response = await fetchProduct();
      setProducts(response || []);
    } catch (err) {
      console.log('Error fetching product list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProductListResponse();
  }, []);

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, products]);

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Product List
        </Typography>
      </Box>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by product name or status..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={products}
            fileName={`Product_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName="Product"
            buttonLabel="Excel"
            exportKeys={headers.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={headers.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddProduct}
            sx={{
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────── */}
      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={
          searchQuery ? `No results for "${searchQuery}"` : 'No products found in the system.'
        }
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={
          selectedProduct
            ? `Edit Product — ${selectedProduct.productName || selectedProduct.product}`
            : 'Add New Product'
        }
        maxWidth="sm"
        persistent={true}
        fullScreen={isMobile ? true : false}
      >
        <AddProduct
          userAccess={userAccess}
          productData={selectedProduct}
          onRefresh={fetchProductListResponse}
          onClose={handleCloseDialog}
        />
      </CommonDialog>
    </div>
  );
};

export default ProductList;
